import { __awaiter } from "tslib";
import qs from 'qs';
import formatZone from '@/search-for-retail/zone/zone.formatters';
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
/** Path segment for the zone resource. */
const ZONE_PATH = 'zone';
/**
 * Pessimistically patches the `getZoneList` cache after a successful `updateZone` mutation,
 * updating the changed item in-place to preserve row order in the table.
 *
 * Defined outside `injectEndpoints` to forward-reference `zoneApi` safely — by the time
 * this function is called the module has fully initialised.
 */
function patchZoneInList(dispatch, areaId, queryFulfilled) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const { data: updatedZone } = yield queryFulfilled;
            dispatch(zoneApi.util.updateQueryData('getZoneList', { areaId }, (draft) => {
                const index = draft.findIndex((z) => z.id === updatedZone.id);
                if (index !== -1) {
                    draft[index] = Object.assign(Object.assign({}, draft[index]), updatedZone);
                }
            }));
        }
        catch (_a) {
            // mutation failed — no cache to roll back
        }
    });
}
/**
 * RTK Query endpoints for zone data.
 *
 * This module extends the base ccApi with zone query and mutation endpoints.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const zoneApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Fetches the list of zones for a given area.
         *
         * @endpoint GET /{areaId}/zone
         */
        getZoneList: builder.query({
            query: ({ areaId }) => `/${areaId}/${ZONE_PATH}`,
            providesTags: [CC_API_TAGS.ZoneList],
        }),
        /**
         * Fetches a single zone by ID within an area.
         *
         * @endpoint GET /{areaId}/zone/{id}
         */
        getZoneById: builder.query({
            query: ({ areaId, id }) => `/${areaId}/${ZONE_PATH}/${id}`,
            providesTags: (_result, _error, { id }) => [{ type: CC_API_TAGS.Zone, id }],
        }),
        /**
         * Creates a new zone in the given area.
         *
         * @endpoint POST /{areaId}/zone
         */
        createZone: builder.mutation({
            query: ({ areaId, data }) => ({
                url: `/${areaId}/${ZONE_PATH}`,
                method: 'POST',
                body: formatZone(Object.assign({ areaId }, data)),
            }),
            invalidatesTags: [CC_API_TAGS.ZoneList],
        }),
        /**
         * Updates an existing zone.
         *
         * @endpoint PUT /{areaId}/zone/{id}
         */
        updateZone: builder.mutation({
            query: ({ areaId, data }) => ({
                url: `/${areaId}/${ZONE_PATH}/${data.id}`,
                method: 'PUT',
                body: formatZone(data),
            }),
            invalidatesTags: (_result, _error, { data }) => [
                { type: CC_API_TAGS.Zone, id: data.id },
            ],
            onQueryStarted: (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ areaId }, { dispatch, queryFulfilled }) {
                yield patchZoneInList(dispatch, areaId, queryFulfilled);
            }),
        }),
        /**
         * Deletes a single zone from an area.
         *
         * @endpoint DELETE /{areaId}/zone/{id}
         */
        deleteZone: builder.mutation({
            query: ({ areaId, id }) => ({
                url: `/${areaId}/${ZONE_PATH}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { id }) => [
                CC_API_TAGS.ZoneList,
                { type: CC_API_TAGS.Zone, id },
            ],
        }),
        /**
         * Bulk-deletes zones from an area.
         *
         * @endpoint DELETE /{areaId}/zone/bulk/delete?ids=...
         */
        bulkDeleteZone: builder.mutation({
            query: ({ areaId, ids }) => {
                const query = qs.stringify({ ids }, { indices: false });
                return {
                    url: `/${areaId}/${ZONE_PATH}/bulk/delete?${query}`,
                    method: 'DELETE',
                };
            },
            invalidatesTags: [CC_API_TAGS.ZoneList],
        }),
        /**
         * Promotes a zone to another area.
         *
         * @endpoint POST /{areaId}/zone/{zoneId}/promote/{targetAreaId}
         */
        promoteZoneToArea: builder.mutation({
            query: ({ areaId, zoneId, targetAreaId }) => ({
                url: `/${areaId}/${ZONE_PATH}/${zoneId}/promote/${targetAreaId}`,
                method: 'POST',
            }),
            invalidatesTags: [CC_API_TAGS.ZoneList],
        }),
        /**
         * Bulk-promotes zones to another area.
         *
         * @endpoint POST /{areaId}/zone/bulk/promote/{targetAreaId}?ids=...
         */
        bulkPromoteZonesToArea: builder.mutation({
            query: ({ areaId, targetAreaId, ids }) => {
                const query = qs.stringify({ ids }, { indices: false });
                return {
                    url: `/${areaId}/${ZONE_PATH}/bulk/promote/${targetAreaId}?${query}`,
                    method: 'POST',
                };
            },
            invalidatesTags: [CC_API_TAGS.ZoneList],
        }),
    }),
});
export const { useBulkDeleteZoneMutation, useBulkPromoteZonesToAreaMutation, useCreateZoneMutation, useDeleteZoneMutation, useGetZoneByIdQuery, useGetZoneListQuery, useLazyGetZoneByIdQuery, useLazyGetZoneListQuery, usePromoteZoneToAreaMutation, useUpdateZoneMutation, } = zoneApi;
